/**
 * `@dxf-ai-editor/cad-core` — BlockInsert (INSERT) bounds (DEFUSE S10b): the
 * public entry points + the barrel of the INSERT-geometry stack.
 *
 * A BlockInsert's real footprint requires resolving the referenced block
 * DEFINITION's child geometry and applying the INSERT transform (translate →
 * base-point subtract → scale → rotate) — the "block-instance transform
 * chain" edge case, tiled across a MINSERT grid (`columnCount`/`rowCount` >
 * 1 — Autodesk DXF reference, INSERT group 70/71/44/45 "Array" fields) when
 * present.
 *
 * Module map (each a leaf-ward import, no cycles — `.dependency-cruiser.cjs`
 * `no-circular`; the split keeps THIS file as the barrel so no external
 * import path changed):
 *   - `blockLookupContext.ts` — the caller-facing context types
 *     (`BlockLookupContext`, the dynamic hook, the memo alias);
 *   - `blockInsertInstance.ts` — per-instance helpers: MINSERT tiling, the
 *     synthetic MLEADER content INSERT, the dynamic per-instance side table,
 *     the per-instance ATTRIB extent;
 *   - `blockDefinitionWalk.ts` — the per-kind definition walk, the shared
 *     memoize + cycle guard, the STATIC / DYNAMIC regime switch of one
 *     INSERT and its placement chain;
 *   - `blockSelectGeometry.ts` — the SELECT twin (transformed child points +
 *     segments).
 *
 * DYNAMIC blocks (Codex PR #784 round 3 P1): a definition with
 * `dynamicMetadata` renders per-instance EVALUATED children, so its bounds
 * are resolved from the caller-injected evaluation
 * (`BlockLookupContext.resolveDynamicChildren` → `resolveInsertLocalBounds`),
 * memoized per (memo, ancestor chain, instance state) instead of per name;
 * static definitions keep the per-name memo. Block-table reads are own-
 * property-safe (`safeRecordLookup`, round 3 P2) throughout — bounds, select
 * geometry and MLEADER block content alike.
 */

import type { BlockDefinition, BlockInsertEntity } from "@dxf-ai-editor/drawing-model";

import {
  multileaderBlockContentLocalBounds,
  placeInsertInstance,
  resolveInsertLocalBounds,
  type DefinitionWalk,
} from "./blockDefinitionWalk";
import type { MleaderBlockContentRef } from "./blockInsertInstance";
import type {
  BlockDefinitionBoundsMemo,
  BlockLookupContext,
  DimensionStyleTable,
  DynamicChildrenResolver,
} from "./blockLookupContext";
import type { Aabb } from "./common";
import {
  descendNestedInsertFrame,
  WORLD_RECURSION_FRAME,
} from "./recursionPlaceholder";

export type {
  BlockDefinitionBoundsMemo,
  BlockLookupContext,
  DimensionStyleTable,
  DynamicChildrenResolver,
  DynamicInstanceChildren,
} from "./blockLookupContext";
export { multileaderBlockContentSelectGeometry, resolveBlockInsertSelectGeometry } from "./blockSelectGeometry";

function insertionPointBounds(entity: BlockInsertEntity): Aabb {
  const x = entity.insertionPoint.x;
  const y = entity.insertionPoint.y;
  return { minX: x, minY: y, maxX: x, maxY: y };
}

/**
 * Same resolution as {@link resolveBlockInsertBounds}, but returns `null`
 * INSTEAD of the insertion-point-only fallback when the referenced
 * definition has NO usable child geometry (missing / cyclic / every child
 * unsupported or positionless — the internal `ALWAYS_INCLUDE` sentinel) or
 * no `blockDefinitions` context was supplied at all.
 *
 * `resolveBlockInsertBounds` itself must never return `null` — its
 * documented contract ("always a concrete Aabb") feeds render/select/grips
 * consumers that need SOME box even when the real extent is unknown. This
 * sibling exists for callers that need to tell "genuinely unresolved" apart
 * from a legitimate degenerate `{0,0,0,0}` transformed AABB (e.g. a lone
 * Point/Text/Attdef child sitting exactly at the block's local origin) —
 * codex PR #472 finding 2: `computeBlockBounds`'s overlap-warning footprint
 * was substituting a fabricated default box for the latter, which is
 * numerically indistinguishable from the former once collapsed into a
 * single `Aabb`.
 *
 * Definition footprint (memoized — per name for a static definition, per
 * (memo, chain, state) for a dynamic one) placed by the INSERT affine, tiled
 * across the MINSERT grid and UNION'd with THIS instance's own ATTRIB extent
 * (`blockDefinitionWalk.ts::placeInsertInstance` — the same chain a nested
 * `Block` child goes through).
 */
export function resolveBlockInsertBoundsOrNull(
  entity: BlockInsertEntity,
  ctx?: BlockLookupContext,
): Aabb | null {
  if (!ctx?.blockDefinitions) return null;

  // Caller-owned memo when supplied (see `BlockLookupContext.definitionBoundsMemo`);
  // otherwise a fresh per-call map. `inProgress` is ALWAYS fresh — cycle-guard
  // traversal state must not leak between calls.
  const walk: DefinitionWalk = {
    blocks: ctx.blockDefinitions,
    resolved: ctx.definitionBoundsMemo ?? new Map(),
    inProgress: new Set<string>(),
    dynamic: ctx.resolveDynamicChildren,
    dimensionStyles: ctx.dimensionStyles,
    // `resolveInsertLocalBounds` walks INTO this entity's definition, so the
    // walk already carries THAT definition's frame — the top-level INSERT's
    // own transform, descended from the world frame
    // (`recursionPlaceholder.ts`'s `DefinitionWalk.frame` contract).
    frame: descendNestedInsertFrame(WORLD_RECURSION_FRAME, entity),
    placeholders: { count: 0 },
  };
  return placeInsertInstance(entity, resolveInsertLocalBounds(entity, walk), walk.resolved);
}

/**
 * Real model-space AABB of a `Block` (INSERT) entity: the resolved
 * definition's child-geometry bounds transformed by the INSERT (base point
 * subtract → scale → rotate → translate), tiled across a MINSERT grid when
 * present. Falls back to the insertion-point-only degenerate box when no
 * `blockDefinitions` context is supplied, or when the referenced block is
 * missing / cyclic / has no usable geometry (the conservative
 * `ALWAYS_INCLUDE` sentinel) — an honest "extent unknown beyond the
 * placement point", never a fabricated icon box.
 */
export function resolveBlockInsertBounds(
  entity: BlockInsertEntity,
  ctx?: BlockLookupContext,
): Aabb {
  return resolveBlockInsertBoundsOrNull(entity, ctx) ?? insertionPointBounds(entity);
}

/**
 * Model-space AABB of a MULTILEADER's BLOCK content (contentType 1) — the
 * referenced block's flattened footprint placed by
 * `blockInsertInstance.ts::makeMleaderContentInsert`, INCLUDING the MLEADER's
 * own `blockAttributeReferences[]` mapped onto that synthetic INSERT's
 * `attributes[]` and unioned in through the shared `placeInsertInstance` chain
 * (Codex PR #784 round 9 P2 — see `makeMleaderContentInsert`'s doc). Lives here
 * (not in `leafBounds.ts`, which must stay free of the block-walk import that would
 * form a cycle) so `bounds.ts`'s top-level Multileader case can UNION it in;
 * the block-child Multileader case of the walk goes through the same
 * `multileaderBlockContentLocalBounds` with its CURRENT walk.
 *
 * `resolved`/`inProgress` (Codex #467 P2 "Reuse the block cycle guard for
 * MLEADER block content"): when this content is resolved from INSIDE a block
 * definition walk (a block whose child MLEADER's content points back at the
 * SAME definition, directly or through a cycle), the CURRENT
 * memoize+cycle-guard state MUST be threaded through so the shared
 * `resolveDefinitionBounds` sees the in-progress name and yields
 * `ALWAYS_INCLUDE` (→ `null` here) instead of starting a FRESH
 * `resolveBlockInsertBounds` traversal that recurses forever to a stack
 * overflow. The top-level `bounds.ts` caller omits them (a fresh walk, no
 * cycle possible yet). Returns `null` when the block is absent / unresolvable /
 * cyclic (conservative — the caller's leader/anchor bounds stand, never
 * shrink); a missing / empty block name is "no definition" (own-property-safe
 * lookup inside the walk — `blockGeometry.lookup.test.ts` "MLEADER block
 * content naming missing `constructor`").
 */
export function multileaderBlockContentBounds(
  e: MleaderBlockContentRef,
  blocks: Readonly<Record<string, BlockDefinition>>,
  resolved?: BlockDefinitionBoundsMemo,
  inProgress?: Set<string>,
  dynamic?: DynamicChildrenResolver,
  dimensionStyles?: DimensionStyleTable,
): Aabb | null {
  const walk: DefinitionWalk = {
    blocks,
    resolved: resolved ?? new Map(),
    inProgress: inProgress ?? new Set<string>(),
    dynamic,
    dimensionStyles,
    // The MLEADER this content belongs to is a TOP-LEVEL entity here (the
    // block-child case goes through `childEntityBounds`'s own walk), so its
    // frame is the world one; `multileaderBlockContentLocalBounds` descends
    // the content reference itself.
    frame: WORLD_RECURSION_FRAME,
    placeholders: { count: 0 },
  };
  return multileaderBlockContentLocalBounds(e, walk);
}
